import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import RouteIcon from '@mui/icons-material/Route';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const drawerWidth = 240;
const miniDrawerWidth = 65; // Largura do drawer quando recolhido (apenas ícones)

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children, title }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Em dispositivos móveis, começar com o menu fechado
  const [drawerOpen, setDrawerOpen] = useState(window.innerWidth >= 900); 
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  // Detectar mudanças no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 900) {
        setDrawerOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleDrawerToggle = () => {
    // Em dispositivos móveis, alterna o estado do drawer móvel
    if (window.innerWidth < 900) {
      setMobileOpen(!mobileOpen);
    } else {
      // Em desktop, alterna o estado do drawer persistente
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Calendário', icon: <CalendarMonthIcon />, path: '/calendar' },
    { text: 'Clientes', icon: <PeopleIcon />, path: '/clients' },
    { text: 'Ativos', icon: <InventoryIcon />, path: '/assets' },
    { text: 'Categorias', icon: <CategoryIcon />, path: '/categories' },
    { text: 'Ordens de Serviço', icon: <AssignmentIcon />, path: '/service-requests' },
    { text: 'Kanban OS', icon: <ViewKanbanIcon />, path: '/service-requests/kanban' },
    { text: 'Roteirização', icon: <RouteIcon />, path: '/routes' },
    { text: 'Contratos de SLA', icon: <DescriptionIcon />, path: '/slas' },
    { text: 'Templates de Checklist', icon: <ChecklistIcon />, path: '/checklist-templates' },
    { text: 'Dashboard Financeiro', icon: <BarChartIcon />, path: '/financial-dashboard' },
    { text: 'Custos', icon: <ReceiptIcon />, path: '/costs' },
    { text: 'Visitas Comerciais', icon: <BusinessCenterIcon />, path: '/commercial-visits' },
    { text: 'Usuários', icon: <SupervisorAccountIcon />, path: '/users' },
    { text: 'Relatórios', icon: <BarChartIcon />, path: '/reports' },
    { text: 'Configurações', icon: <SettingsIcon />, path: '/settings' },
  ];

  const drawer = (
    <div>
      {/* Adicionamos um Toolbar vazio para manter o alinhamento com o AppBar */}
      <Toolbar sx={{ minHeight: 64 }} /> {/* Altura fixa para corresponder ao AppBar */}
      <List sx={{ pt: 0 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              onClick={() => {
                console.log(`Navegando para: ${item.path}`);
                navigate(item.path);
              }}
              sx={{
                minHeight: 48,
                justifyContent: drawerOpen ? 'initial' : 'center',
                pl: drawerOpen ? 2 : 0, // Padding esquerdo fixo para alinhar com o cabeçalho
                pr: drawerOpen ? 2 : 0, // Padding direito fixo
              }}
            >
              <Tooltip title={drawerOpen ? '' : item.text} placement="right" arrow>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto', // Reduzido para 2 para alinhar melhor com o botão do menu
                    justifyContent: 'center',
                    ml: drawerOpen ? 0 : 'auto', // Centralizar ícones quando recolhido
                    width: drawerOpen ? 'auto' : '100%', // Largura total quando recolhido
                    textAlign: 'center', // Centralizar ícones
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.25rem', // Tamanho consistente para ícones
                    }
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </Tooltip>
              <ListItemText 
                primary={item.text} 
                sx={{ 
                  opacity: drawerOpen ? 1 : 0,
                  display: drawerOpen ? 'block' : 'none' 
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          backgroundColor: '#2e7d32',
          zIndex: (theme) => theme.zIndex.drawer + 1, // Garantir que o AppBar fique acima do drawer
        }}
      >
        <Toolbar sx={{ 
          padding: 0, 
          minHeight: { xs: 56, sm: 64 }, // Altura responsiva
          px: { xs: 1, sm: 2 } // Padding horizontal responsivo
        }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              width: { 
                xs: 'auto',
                md: drawerOpen ? drawerWidth : miniDrawerWidth
              },
              justifyContent: { 
                xs: 'flex-start',
                md: drawerOpen ? 'flex-start' : 'center'
              },
              pl: { xs: 0, sm: 1, md: 2 }, // Padding responsivo
              pr: { xs: 1, sm: 2 }, // Padding responsivo
              height: { xs: 56, sm: 64 }, // Altura responsiva
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              sx={{ 
                p: 0.5,
                mr: drawerOpen ? 1.5 : 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <MenuIcon sx={{ fontSize: '1.25rem' }} /> {/* Mesmo tamanho dos outros ícones */}
            </IconButton>
            {drawerOpen && (
              <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
                Clarens Act Field
              </Typography>
            )}
          </Box>
          <Divider orientation="vertical" flexItem sx={{ mr: 2, bgcolor: 'rgba(255,255,255,0.5)' }} />
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' }, // Fonte responsiva
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {title}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#1b5e20' }}>
              {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              navigate('/profile');
            }}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Perfil</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sair</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ 
          width: drawerOpen ? drawerWidth : miniDrawerWidth, 
          flexShrink: 0,
          transition: theme => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Drawer para dispositivos móveis */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Melhor performance em dispositivos móveis
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              zIndex: (theme) => theme.zIndex.drawer + 2
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Drawer para desktop */}
        <Drawer
          variant="permanent" // Alterado para permanent para sempre mostrar (expandido ou mini)
          open={true}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerOpen ? drawerWidth : miniDrawerWidth,
              overflowX: 'hidden', // Evita barra de rolagem horizontal durante a transição
              transition: theme => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 }, // Padding responsivo
          width: { 
            xs: '100%', 
            md: `calc(100% - ${drawerOpen ? drawerWidth : miniDrawerWidth}px)` 
          },
          mt: { xs: '56px', sm: '64px' }, // Altura do AppBar responsiva
          transition: theme => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          height: { 
            xs: 'calc(100vh - 56px)', // Altura para mobile
            sm: 'calc(100vh - 64px)'  // Altura para desktop
          }, 
          overflow: 'auto', // Adiciona scroll ao conteúdo principal
          position: 'relative', // Para posicionamento absoluto de elementos filhos
          '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: '#f5f5f5'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ddd',
            borderRadius: '4px'
          }
        }}
      >
        <Toolbar sx={{ display: { xs: 'none', sm: 'none' } }} /> {/* Espaçador para dispositivos móveis */}
        {children}
      </Box>
    </Box>
  );
};

export default AuthLayout;
